/**
 * Renders ONE backend message (§5) from the Product Catalog Control Center AI
 * chatbot (Brisque AI), switching on `kind`. Card edits/confirm/sync/fix actions
 * dispatch through the `usePCChatbot` hook. Channel ids are resolved to names via
 * the fetched channels list (§4.8). Purely presentational otherwise.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PCChatbot } from '../hooks/usePCChatbot';
import { Rich, Icon, Chip, Pill, PrimaryBtn, GhostBtn, FONT } from './atoms';
import type {
    PCMessage,
    PCActionCard,
    PCDisambiguationCandidate,
    PCActionRow,
    PCStateCard,
    PCDiagnosticCard,
    PCDiagnosticMultiCard,
    PCSyncCard,
    PCDuration,
    PCFix,
    PCBotTextMessage,
} from '../../../interface/ProductCatalogChatbotInterface';
// Product Catalog's own date/time picker, reused verbatim so a scheduled restore is chosen the
// same way here as it is on the Control Centre screen behind the assistant.
import { CatalogDateTimePicker, todayIsoLocal } from '../../productCatalog/components/ui/CatalogDateTimePicker';
import AiAgentLogo from '../../../assets/images/AI_Agent_Logo.svg';

/** The four durations Product Catalog itself offers — kept in step with the PC modal so the
 *  bot never presents a choice the rest of the product does not have. "Until specific time"
 *  carries no fixed moment: picking it reveals a date+time input (SpecificTimePicker below). */
const SPECIFIC_TIME_LABEL = 'Until specific time';
const DURATION_PRESETS: PCDuration[] = [
    { mode: 'next_session', label: 'Until next session' },
    { mode: 'end_of_day', label: 'End of day' },
    { mode: 'specific_time', label: SPECIFIC_TIME_LABEL },
    { mode: 'manual', label: 'Until manually re-enabled' },
];

/** What the picker should show: the full moment, the date alone mid-selection, or nothing. */
const pickerValue = (d: PCDuration): string => {
    if (!d.atDate) return '';
    return d.atTime ? `${d.atDate}T${d.atTime}` : d.atDate;
};

/**
 * Label for a moment the user picked, e.g. "Sep 12, 3:40 PM".
 *
 * Built from the date and time PARTS rather than `new Date(value)`, so the string the user sees
 * is always exactly the one they chose - no timezone reinterpretation between picking and
 * displaying.
 */
const formatPickedMoment = (atDate: string, atTime: string): string => {
    const [y, mo, d] = atDate.split('-').map(Number);
    const [h, mi] = atTime.split(':').map(Number);
    if (![y, mo, d, h, mi].every(Number.isFinite)) return `${atDate} ${atTime}`;
    return new Intl.DateTimeFormat(undefined, {
        timeZone: 'UTC', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }).format(new Date(Date.UTC(y, mo - 1, d, h, mi)));
};

const toneColor: Record<string, { c: string; bg: string; icon: string }> = {
    warn: { c: 'var(--orange)', bg: 'rgba(217,119,6,0.07)', icon: 'info' },
    error: { c: 'var(--red)', bg: 'rgba(220,38,38,0.07)', icon: 'error' },
    success: { c: 'var(--green)', bg: 'rgba(22,163,74,0.07)', icon: 'check_circle' },
};

/**
 * Card wrapper. `spent` greys the whole card and blocks pointer interaction — used once an
 * actionable card has been acted on (confirmed/cancelled/picked/synced) or has an action in
 * flight, so the same action can never be triggered twice (§Item 1). It stays fully readable
 * (the record of what was done) but is visibly, unmistakably inert.
 *
 * READ-ONLY controls are the exception. A bulk card collapses past ROW_COLLAPSE_THRESHOLD
 * rows behind a "Show N more" toggle, and blanket `pointer-events: none` killed that toggle
 * too — so after confirming a change across many items the user could no longer expand the
 * card to see WHICH items were changed. A read-only control re-enables itself with
 * `pointerEvents: 'auto'`; nothing that writes ever does.
 */
const CardShell: React.FC<{ children: React.ReactNode; accent?: string; spent?: boolean }> = ({ children, accent = 'var(--accent)', spent }) => (
    <div
        aria-disabled={spent || undefined}
        style={{
            border: '1px solid var(--card-border)', borderLeft: `3px solid ${accent}`,
            borderRadius: 'var(--radius-card)', background: 'var(--surface)', boxShadow: 'var(--card-shadow)',
            overflow: 'hidden', width: '100%', maxWidth: 460,
            opacity: spent ? 0.62 : 1,
            pointerEvents: spent ? 'none' : undefined,
            filter: spent ? 'grayscale(0.35)' : undefined,
            transition: 'opacity .2s ease, filter .2s ease',
        }}
    >
        {children}
    </div>
);

const Head: React.FC<{ icon: string; title?: string; subtitle?: string; accent?: string }> = ({ icon, title, subtitle, accent = 'var(--accent)' }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid var(--divider)' }}>
        <div style={{ width: 30, height: 30, borderRadius: 9, background: 'var(--accent-soft, rgba(210,58,59,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name={icon} size={17} color={accent} />
        </div>
        <div style={{ minWidth: 0 }}>
            {title && <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--t1)', fontFamily: FONT }}>{title}</div>}
            {subtitle && <div style={{ fontSize: 11, color: 'var(--t3)', fontFamily: FONT, marginTop: 1 }}>{subtitle}</div>}
        </div>
    </div>
);

/** One-tap fix buttons (§5.2/5.4/5.5) — send the instruction as a new message. */
const FixButtons: React.FC<{ fixes?: PCFix[]; onSend: (text: string, fromAffordance?: boolean) => void }> = ({ fixes, onSend }) =>
    fixes && fixes.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 14px 12px' }}>
            {fixes.map((f, i) => (
                <GhostBtn key={i} icon="build" small onClick={() => onSend(f.instruction, true)}>{f.label}</GhostBtn>
            ))}
        </div>
    ) : null;

/* ─────────────────────────── bot-text (§5.2) ─────────────────────────── */

const BotText: React.FC<{ msg: PCBotTextMessage; onSend: (text: string, fromAffordance?: boolean) => void }> = ({ msg, onSend }) => {
    const t = msg.tone ? toneColor[msg.tone] : undefined;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    <img src={AiAgentLogo} alt="" width={26} height={26} style={{ borderRadius: 8 }} />
                </div>
                {/* pre-line so newlines in the message survive: a bulk confirmation groups its
                    items one per line, and without this they collapse into one stuffed paragraph. */}
                <div style={{ fontSize: 13.5, lineHeight: 1.5, color: t ? t.c : 'var(--t1)', fontFamily: FONT, background: t ? t.bg : 'transparent', padding: t ? '9px 12px' : '3px 0', borderRadius: t ? 12 : 0, border: t ? `1px solid ${t.c}22` : 'none', whiteSpace: 'pre-line' }}>
                    {t && <Icon name={t.icon} size={14} color={t.c} style={{ verticalAlign: -2, marginRight: 5 }} />}
                    <Rich text={msg.text} boldColor={t?.c} />
                </div>
            </div>
            {msg.fixes && msg.fixes.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginLeft: 35 }}>
                    {msg.fixes.map((f, i) => (
                        <GhostBtn key={i} icon="bolt" small onClick={() => onSend(f.instruction, true)}>{f.label}</GhostBtn>
                    ))}
                </div>
            )}
        </div>
    );
};

/* ─────────────────────────── Action card (§6) ─────────────────────────── */

const TYPE_META: Record<string, { label: string; accent: string; icon: string }> = {
    availability: { label: 'Availability', accent: 'var(--orange)', icon: 'inventory' },
    visibility: { label: 'Visibility', accent: '#7C3AED', icon: 'visibility' },
    session: { label: 'Session', accent: '#0EA5E9', icon: 'schedule' },
};

// Pending cards read as a sentence with the entity name above ("Chicken 65 — will be marked out of
// stock"), in the four words merchants act on: out of stock / available / hidden / visible.
const opTitle = (row: PCActionRow, pending: boolean): string => {
    if (row.type === 'availability') {
        if (row.op === 'restore') return pending ? 'Will be marked available' : 'Mark available';
        return pending ? 'Will be marked out of stock' : 'Mark out of stock';
    }
    if (row.type === 'visibility') {
        if (row.op === 'show') return pending ? 'Will be made visible' : 'Make visible';
        return pending ? 'Will be hidden' : 'Hide';
    }
    return 'Change sessions';
};

const RowView: React.FC<{
    row: PCActionRow; editable: boolean; multi: boolean; chat: PCChatbot;
    onChannels: (ids: string[]) => void; onDuration: (d: PCDuration) => void; onRemove: () => void;
}> = ({ row, editable, multi, chat, onChannels, onDuration, onRemove }) => {
    // Was the picker OPENED BY THE USER on this row (picked "Until specific time" from the
    // dropdown), as opposed to a duration the agent already resolved from words like "till next
    // Sunday"? Both can look identical in the data - {atDate, no atTime} - so the data alone
    // can't tell them apart: an agent-resolved date-only duration must show as settled text, but
    // a date the USER is mid-way through picking (chose the date, hasn't reached time yet) must
    // keep the picker open. This is set true only by the dropdown's own onChange, never inferred
    // from row.duration's shape, so a "till next Thursday" reply never re-opens an empty picker
    // under an answer the user never asked to edit.
    const [pickerOpenedByUser, setPickerOpenedByUser] = React.useState(false);
    const meta = TYPE_META[row.type] ?? TYPE_META.availability;
    const allChannels = chat.state.channels;
    // Channels the server froze for this row (e.g. availability on a channel where the item
    // isn't even enabled) must be shown as DISABLED chips — never clickable — so the card's
    // controls can't contradict its own "can't change X here" notice (§blocked-channel UX).
    const blockedSet = new Set((row.blockedChannels ?? []).map((b) => b.channel));
    const blockedReason = (id: string): string | undefined => row.blockedChannels?.find((b) => b.channel === id)?.reason;
    // Channels already in the requested state — a no-op with nothing to write. Also locked, but
    // shown as "already done" (a satisfied ✓ chip) rather than a blocked one, so the user isn't
    // invited to toggle a channel that would change nothing (e.g. "enable on Delivery" when it's
    // already enabled there).
    const alreadySet = new Set(row.alreadyChannels ?? []);
    const verb = row.type === 'availability'
        ? (row.op === 'restore' ? 'available' : 'out of stock')
        : (row.op === 'show' ? 'visible' : 'hidden');
    const toggle = (id: string) => {
        if (blockedSet.has(id) || alreadySet.has(id)) return; // frozen / no-op — ignore taps
        const has = row.channels.includes(id);
        onChannels(has ? row.channels.filter((x) => x !== id) : [...row.channels, id]);
    };
    return (
        <div style={{ padding: '11px 14px', borderTop: '1px solid var(--divider)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, background: 'var(--bg)', color: meta.accent, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3, textTransform: 'uppercase', fontFamily: FONT }}>
                        <Icon name={meta.icon} size={12} color={meta.accent} />{meta.label}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)', fontFamily: FONT }}>{row.entityName}</span>
                </div>
                {multi && editable && (
                    <button type="button" onClick={onRemove} title="Remove" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--t3)', display: 'flex' }}>
                        <Icon name="close" size={16} />
                    </button>
                )}
            </div>

            <div style={{ fontSize: 12, color: 'var(--t2)', fontFamily: FONT, marginTop: 4 }}>{opTitle(row, editable)}</div>

            {row.type === 'visibility' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, padding: '6px 9px', borderRadius: 8, background: 'rgba(124,58,237,0.07)', color: '#7C3AED', fontSize: 11.5, fontWeight: 600, fontFamily: FONT }}>
                    <Icon name="push_pin" size={13} /> This changes where it appears permanently — not just today.
                </div>
            )}

            {row.type === 'session' ? (
                row.sessionLink && (
                    <div style={{ marginTop: 6, fontSize: 12, fontFamily: FONT, color: 'var(--t2)' }}>
                        {row.sessionLink.itemName || row.entityName}
                        {row.sessionLink.sessionName ? ` → ${row.sessionLink.sessionName}` : ''}
                    </div>
                )
            ) : (
                <>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 9, fontFamily: FONT }}>Channels</div>
                    {editable ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                            {allChannels.map((c) => {
                                const blocked = blockedSet.has(c.id);
                                const already = !blocked && alreadySet.has(c.id);
                                const locked = blocked || already;
                                const tip = blocked
                                    ? (blockedReason(c.id) || "Can't change this channel here")
                                    : already
                                        ? `Already ${verb} here — nothing to change`
                                        : undefined;
                                return (
                                    <span key={c.id} title={tip} style={{ display: 'inline-flex' }}>
                                        <Chip
                                            label={c.name}
                                            // "already" chips read as satisfied (soft green + check); "blocked" as inert.
                                            active={already || row.channels.includes(c.id)}
                                            tone={already ? 'success' : 'accent'}
                                            onClick={locked ? undefined : () => toggle(c.id)}
                                            disabled={locked}
                                            icon={blocked ? 'visibility_off' : already ? 'check' : undefined}
                                            small
                                        />
                                    </span>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{ fontSize: 12, color: 'var(--t1)', fontFamily: FONT, marginTop: 3 }}>{(row.channelNames ?? row.channels.map(chat.channelName)).join(', ')}</div>
                    )}
                </>
            )}

            {row.type === 'availability' && row.op === 'oos' && (
                <>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 9, fontFamily: FONT }}>Duration</div>
                    {editable ? (
                        <select
                            value={row.duration?.label ?? ''}
                            onChange={(e) => {
                                const found = DURATION_PRESETS.find((d) => d.label === e.target.value) ?? DURATION_PRESETS[0];
                                setPickerOpenedByUser(found.label === SPECIFIC_TIME_LABEL);
                                onDuration(found);
                            }}
                            style={{ marginTop: 6, padding: '6px 8px', borderRadius: 'var(--radius-input)', border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--t1)', fontSize: 12, fontFamily: FONT, fontWeight: 600, cursor: 'pointer' }}
                        >
                            {row.duration && !DURATION_PRESETS.some((d) => d.label === row.duration!.label) && (
                                <option value={row.duration.label}>{row.duration.label}</option>
                            )}
                            {DURATION_PRESETS.map((d) => <option key={d.label} value={d.label}>{d.label}</option>)}
                        </select>
                    ) : (
                        <div style={{ fontSize: 12, color: 'var(--t1)', fontFamily: FONT, marginTop: 3 }}>{row.duration?.label}</div>
                    )}
                    {/* Only while the user OPENED this picker themselves via the dropdown. A
                        duration the agent already resolved from their words ("till next
                        Sunday", "till 7th September, 5pm") is a settled answer - showing an
                        empty date/time picker under it asked the user to re-supply what they
                        had just said, even when they never touched the dropdown at all. */}
                    {editable && row.duration?.mode === 'specific_time' && pickerOpenedByUser && (
                        <div style={{ marginTop: 8 }}>
                            <CatalogDateTimePicker
                                value={pickerValue(row.duration)}
                                min={todayIsoLocal()}
                                onChange={(v) => {
                                    // The picker reports the DATE as soon as it is chosen and adds
                                    // "THH:mm" once a time is picked too. Both are kept: dropping the
                                    // date-only value made the calendar look dead - a date could not
                                    // be selected at all until a time already existed.
                                    if (!v) {
                                        onDuration({ mode: 'specific_time', label: SPECIFIC_TIME_LABEL });
                                        return;
                                    }
                                    const atDate = v.slice(0, 10);
                                    const atTime = v.length >= 16 ? v.slice(11, 16) : undefined;
                                    onDuration({
                                        mode: 'specific_time',
                                        atDate,
                                        ...(atTime ? { atTime } : {}),
                                        label: atTime
                                            ? `Until ${formatPickedMoment(atDate, atTime)}`
                                            : SPECIFIC_TIME_LABEL,
                                    });
                                }}
                            />
                        </div>
                    )}
                </>
            )}

            {row.blockedChannels && row.blockedChannels.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    <Pill tone="bad"><Icon name="visibility_off" size={12} /> Can't change {row.blockedChannels.map((b) => b.channelName ?? chat.channelName(b.channel)).join(', ')}</Pill>
                    {/* Every reason, each with the channels it applies to — channels can be blocked
                        for different reasons (category hidden on one, the item itself on another). */}
                    {Array.from(row.blockedChannels.reduce((m, b) => {
                        if (b.reason) m.set(b.reason, [...(m.get(b.reason) ?? []), b.channelName ?? chat.channelName(b.channel)]);
                        return m;
                    }, new Map<string, string[]>())).map(([reason, names]) => (
                        <div key={reason} style={{ fontSize: 11, color: 'var(--t3)', marginTop: 3, fontFamily: FONT }}>
                            {row.blockedChannels!.length > 1 ? `${names.join(', ')}: ${reason}` : reason}
                        </div>
                    ))}
                </div>
            )}
            {row.note && <div style={{ marginTop: 8 }}><Pill tone="info"><Icon name="info" size={12} /> {row.note}</Pill></div>}
            {row.warning && <div style={{ marginTop: 8 }}><Pill tone="warn"><Icon name="warning" size={12} /> {row.warning}</Pill></div>}
            {row.withheld && <div style={{ marginTop: 8 }}><Pill tone="bad"><Icon name="lock" size={12} /> Withheld</Pill></div>}
        </div>
    );
};

/** The "which one did you mean?" picker, paginated at PAGE_SIZE so a query matching many items
 *  (e.g. "fries" → 8) shows a scannable page at a time with Prev/Next, not a wall of rows. */
const DISAMBIG_PAGE_SIZE = 5;
const DisambiguationView: React.FC<{ card: PCActionCard; chat: PCChatbot }> = ({ card, chat }) => {
    const candidates = card.candidates ?? [];
    // A plural request ("disable all veg biryani items") lets the user tick several instead of
    // forcing one choice. `preselectAll` is only set when the backend matched on exact names, so a
    // fuzzy "did you mean?" list starts empty rather than arming a write on items nobody named.
    const multi = !!card.multiSelect;
    // Pre-ticking must skip anything a pre-ticked CATEGORY already covers, or the selection starts
    // in the one state the server refuses ("Ghee Idly is already inside Idly") — the user would hit
    // that on their first Continue without having chosen the combination at all.
    const [ticked, setTicked] = React.useState<string[]>(() => {
        if (!multi) return [];
        // `preselectIds` ticks a chosen subset (a category's own items, not the look-alikes offered
        // after them); `preselectAll` ticks every row. Neither → start empty.
        const all = card.preselectIds
            ? candidates.map((c) => c.id).filter((id) => card.preselectIds!.includes(id))
            : card.preselectAll ? candidates.map((c) => c.id) : [];
        return all.filter((id) => {
            const cand = candidates.find((c) => c.id === id);
            return !(cand?.coveredBy ?? []).some((parent) => all.includes(parent));
        });
    });
    // Ticking a category drops anything already ticked that it covers, so the selection can never
    // hold both a category and something inside it (which the server would refuse).
    const toggle = (id: string) =>
        setTicked((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            const kept = prev.filter((x) => {
                const cand = candidates.find((c) => c.id === x);
                return !(cand?.coveredBy ?? []).includes(id);
            });
            return [...kept, id];
        });

    // Ticking a category makes the rows it CONTAINS redundant — category visibility cascades, so
    // changing the category already changes them. Those rows go inert the moment it's ticked,
    // rather than the server rejecting the combination after Continue. Only real containment
    // counts (`coveredBy`, computed server-side): a category and a similarly-named dish filed
    // elsewhere are unrelated, and both stay pickable.
    const blockedBy = (c: PCDisambiguationCandidate) =>
        (c.coveredBy ?? []).filter((id) => ticked.includes(id));
    const blocked = (c: PCDisambiguationCandidate) => multi && blockedBy(c).length > 0;
    const nameOf = (id: string) => candidates.find((x) => x.id === id)?.name ?? '';

    // "Select all" takes every row EXCEPT those a category in the same sweep would cover, so it can
    // never build a set the server refuses. Computed against the full list rather than `blocked`
    // (which depends on what is currently ticked) so the count doesn't shift as boxes change.
    const selectAllSet = candidates
        .filter((c) => !(c.coveredBy ?? []).some((parent) => candidates.some((x) => x.id === parent)))
        .map((c) => c.id);
    const allTicked = selectAllSet.length > 0 && ticked.length === selectAllSet.length;
    const [page, setPage] = React.useState(0);
    const pageCount = Math.ceil(candidates.length / DISAMBIG_PAGE_SIZE);
    const safePage = Math.min(page, Math.max(0, pageCount - 1));
    const start = safePage * DISAMBIG_PAGE_SIZE;
    const shown = candidates.slice(start, start + DISAMBIG_PAGE_SIZE);

    const pagerBtn = (disabled: boolean): React.CSSProperties => ({
        display: 'flex', alignItems: 'center', gap: 3, padding: '5px 9px', borderRadius: 8,
        border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--t2)',
        fontFamily: FONT, fontSize: 12, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1,
    });

    // A pick in flight (cardActionLoading) freezes the whole picker so a second candidate
    // can't be tapped before the first resolves — the optimistic patch then retires it and
    // this view stops rendering entirely (status flips off 'disambiguation').
    const busy = chat.state.cardActionLoading;

    // This picker is one clause of a bigger request when more clarifications are queued behind
    // it (`queue`) or clear changes are riding along waiting to be confirmed (`deferredRows`).
    // Declining it then skips only this question, so the escape hatch is labelled accordingly.
    const hasMoreWork = !!(card.queue?.length || card.deferredRows?.length);

    return (
        <CardShell accent="#0EA5E9" spent={busy}>
            <Head
                icon="alt_route"
                title={card.title || 'Which one did you mean?'}
                subtitle={multi
                    ? `${candidates.length} matches — tick every one you meant`
                    : candidates.length > DISAMBIG_PAGE_SIZE ? `${candidates.length} matches — pick the one you meant` : 'Pick the one you meant'}
                accent="#0EA5E9"
            />
            <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {multi && candidates.length > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 2 }}>
                        <button type="button" disabled={busy}
                            onClick={() => setTicked(allTicked ? [] : selectAllSet)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--t2)', fontFamily: FONT, fontSize: 12, cursor: busy ? 'default' : 'pointer' }}>
                            <Icon name={allTicked ? 'check_box' : 'check_box_outline_blank'} size={15} color={allTicked ? '#0EA5E9' : 'var(--t3)'} />
                            {allTicked ? 'Clear all' : 'Select all'}
                        </button>
                        <span style={{ fontSize: 11, color: 'var(--t3)', fontFamily: FONT }}>
                            {ticked.length} of {candidates.length} selected
                        </span>
                    </div>
                )}
                {shown.map((c) => (
                    <button key={c.id} type="button" disabled={busy || blocked(c)}
                        onClick={() => (multi ? toggle(c.id) : chat.pickCandidate(card.id, c.id))}
                        aria-pressed={multi ? ticked.includes(c.id) : undefined}
                        title={blocked(c) ? `Already covered by ${blockedBy(c).map(nameOf).join(', ')}` : undefined}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--card-border)', background: 'var(--surface)', cursor: blocked(c) ? 'not-allowed' : 'pointer', fontFamily: FONT, textAlign: 'left', opacity: blocked(c) ? 0.45 : 1 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {multi && (
                                <Icon
                                    name={ticked.includes(c.id) ? 'check_box' : 'check_box_outline_blank'}
                                    size={16}
                                    color={ticked.includes(c.id) ? '#0EA5E9' : 'var(--t3)'}
                                />
                            )}
                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>{c.name}</span>
                            {/* A picker can mix items and categories (a name like "Idly" may be
                                either). The badge says which, so it's clear when a row would
                                change a WHOLE category rather than one item. */}
                            <span style={{
                                fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
                                color: c.kind === 'category' ? '#7C3AED' : 'var(--t3)',
                                background: c.kind === 'category' ? 'rgba(124,58,237,0.10)' : 'var(--divider)',
                                borderRadius: 4, padding: '1.5px 5px', flexShrink: 0,
                            }}>
                                {c.kind === 'category' ? 'Category' : 'Item'}
                            </span>
                        </span>
                        {/* An item filed in several categories has a long "in A, B, C…" line — cut it
                            to one line inside the row; the full list shows on hover. */}
                        {c.subtitle && <span title={c.subtitle} style={{ fontSize: 11, color: 'var(--t3)', marginLeft: 8, minWidth: 0, maxWidth: '45%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subtitle}</span>}
                    </button>
                ))}

                {pageCount > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 4 }}>
                        <button type="button" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} style={pagerBtn(safePage === 0)}>
                            <Icon name="chevron_left" size={15} /> Prev
                        </button>
                        <span style={{ fontSize: 11, color: 'var(--t3)', fontFamily: FONT }}>Page {safePage + 1} of {pageCount}</span>
                        <button type="button" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} style={pagerBtn(safePage >= pageCount - 1)}>
                            Next <Icon name="chevron_right" size={15} />
                        </button>
                    </div>
                )}

                {/* The composer is locked while this picker is up, so "just type something else"
                    is no longer a way out — Cancel IS the escape hatch, and it must always be
                    present or the user is trapped with no path forward.

                    When this picker is one clause of a compound request, cancelling skips only
                    THIS question and the rest of the request carries on, so the control has to
                    say so — "Cancel" alone read as abandoning everything. */}
                {/* Multi-select needs an explicit commit — ticking a box can't itself resolve the
                    picker the way a single tap does, or the first tick would end the question. */}
                {multi && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 6 }}>
                        <button type="button" disabled={busy || ticked.length === 0}
                            onClick={() => chat.pickCandidates(card.id, ticked)}
                            style={{ padding: '7px 14px', borderRadius: 9, border: '1px solid #0EA5E9', background: '#0EA5E9', color: '#fff', fontFamily: FONT, fontSize: 12.5, fontWeight: 700, cursor: busy || ticked.length === 0 ? 'default' : 'pointer', opacity: busy || ticked.length === 0 ? 0.5 : 1 }}>
                            Continue{ticked.length > 0 ? ` with ${ticked.length}` : ''}
                        </button>
                        {ticked.length === 0 && (
                            <span style={{ fontSize: 11, color: 'var(--t3)', fontFamily: FONT }}>Tick the ones you meant.</span>
                        )}
                        {candidates.some(blocked) && (
                            <span style={{ fontSize: 11, color: 'var(--t3)', fontFamily: FONT }}>
                                Greyed rows are already covered by a category you picked.
                            </span>
                        )}
                    </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 4 }}>
                    <GhostBtn onClick={() => chat.cancelCard(card.id)} disabled={busy}>
                        {hasMoreWork ? 'Skip this' : 'Cancel'}
                    </GhostBtn>
                    <span style={{ fontSize: 11, color: 'var(--t3)', fontFamily: FONT }}>
                        {hasMoreWork
                            ? 'None of these? Skip just this one — the rest of your request still goes ahead.'
                            : 'None of these? Cancel and rephrase.'}
                    </span>
                </div>
            </div>
        </CardShell>
    );
};

/** A card proposing many changes at once ("86 every dosa") renders one full RowView per item —
 *  past a handful that's an unreadable wall, so collapse to the first few with a toggle. Mirrors
 *  the pagination DisambiguationView already uses for long candidate lists. */
const ROW_COLLAPSE_THRESHOLD = 4;

const ActionCardView: React.FC<{ card: PCActionCard; chat: PCChatbot }> = ({ card, chat }) => {
    const [expanded, setExpanded] = React.useState(false);

    if (card.status === 'disambiguation' && card.candidates) {
        return <DisambiguationView card={card} chat={chat} />;
    }

    const editable = card.status === 'pending';
    const cancelled = card.status === 'cancelled';
    const committed = card.status === 'committed';
    // A picker that has been ANSWERED is retired the same way a cancelled card is
    // (status 'cancelled', candidates cleared), but the two mean opposite things to the user:
    // one is "your pick went through", the other is "nothing was applied". A picker only ever
    // asks a question — it carries no rows — whereas a real action card always carries the rows
    // it proposed, so an empty row list is what separates them. Without this, picking an option
    // showed "Not applied" and read as though the selection had failed.
    // The server says HOW a retired card got there (`resolution`), because answering a picker
    // and dropping a proposal both land on status 'cancelled' and mean opposite things. Older
    // cards predate the field, so fall back to the previous heuristic: a picker only ever asks
    // a question and carries no rows, while a real action card always carries its proposed rows.
    const answeredPicker = cancelled && (card.resolution === 'picked' || (!card.resolution && card.rows.length === 0));
    const multi = card.rows.length > 1;
    const noChannels = card.rows.some((r) => r.type !== 'session' && r.channels.length === 0);
    // Once confirmed or cancelled the card is a spent record — greyed and fully inert so its
    // Confirm/Cancel/edit controls can never be triggered again (§Item 1).
    const spent = committed || cancelled;

    const collapsible = card.rows.length > ROW_COLLAPSE_THRESHOLD;
    const shownRows = collapsible && !expanded ? card.rows.slice(0, ROW_COLLAPSE_THRESHOLD) : card.rows;
    const hiddenCount = card.rows.length - shownRows.length;

    return (
        <CardShell accent={cancelled ? 'var(--t3)' : 'var(--accent)'} spent={spent}>
            <Head
                icon={committed ? 'check_circle' : answeredPicker ? 'task_alt' : cancelled ? 'cancel' : 'bolt'}
                title={card.title || 'Review this change'}
                subtitle={multi ? `${card.rows.length} changes · each logged separately` : undefined}
            />
            {shownRows.map((row) => (
                <RowView
                    key={row.id} row={row} editable={editable} multi={multi} chat={chat}
                    onChannels={(ids) => chat.setRowChannels(card.id, row.id, ids)}
                    onDuration={(d) => chat.setRowDuration(card.id, row.id, d)}
                    onRemove={() => chat.removeRow(card.id, row.id)}
                />
            ))}
            {collapsible && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    style={{
                        width: '100%', padding: '9px 14px', border: 'none', borderTop: '1px solid var(--divider)',
                        background: 'transparent', color: 'var(--t2)', fontFamily: FONT, fontSize: 12,
                        fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 6,
                        // Read-only: reveals rows already on the card, writes nothing. Stays
                        // usable after Confirm so a bulk change can still be reviewed.
                        pointerEvents: 'auto',
                    }}
                >
                    <Icon name={expanded ? 'expand_less' : 'expand_more'} size={16} />
                    {expanded ? 'Show less' : `Show ${hiddenCount} more ${hiddenCount === 1 ? 'change' : 'changes'}`}
                </button>
            )}
            <div style={{ padding: '11px 14px', borderTop: '1px solid var(--divider)', display: 'flex', alignItems: 'center', gap: 8 }}>
                {committed ? (
                    <Pill tone="ok"><Icon name="check" size={13} /> Confirmed</Pill>
                ) : answeredPicker ? (
                    <Pill tone="info">
                        <Icon name="check" size={13} />{' '}
                        {card.resolutionLabel ? `${card.resolutionLabel} selected` : 'Selection made'}
                    </Pill>
                ) : cancelled ? (
                    <Pill tone="info">Cancelled the request</Pill>
                ) : (
                    <>
                        <PrimaryBtn icon="check" onClick={() => chat.confirm(card.id)} disabled={noChannels || chat.state.cardActionLoading}>Confirm</PrimaryBtn>
                        <GhostBtn onClick={() => chat.cancelCard(card.id)}>Cancel</GhostBtn>
                        {noChannels && <span style={{ fontSize: 11, color: 'var(--orange)', fontFamily: FONT }}>Select at least one channel</span>}
                    </>
                )}
            </div>
        </CardShell>
    );
};

/* ─────────────────────────── State card (§5.3) ─────────────────────────── */

const Dot: React.FC<{ on: boolean; color: string }> = ({ on, color }) => (
    <span style={{ width: 9, height: 9, borderRadius: '50%', background: on ? color : 'transparent', border: `1.5px solid ${on ? color : 'var(--card-border)'}`, display: 'inline-block' }} />
);

const StateCardView: React.FC<{ card: PCStateCard; chat: PCChatbot }> = ({ card, chat }) => (
    <CardShell accent="#0EA5E9">
        <Head icon="travel_explore" title={card.title} subtitle={card.subtitle} accent="#0EA5E9" />
        <div style={{ padding: 12 }}>
            {card.mode === 'entity-grid' && card.cells && (() => {
                // Only show the Available column when the entity actually HAS stock state.
                // A CATEGORY has none (out-of-stock is item-level), so the backend omits
                // `available` on its cells and this drops the column rather than drawing a
                // green dot for a state categories do not have.
                const showAvailable = card.cells.some((c) => c.available !== undefined);
                return (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
                    <thead>
                        <tr style={{ fontSize: 10.5, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 700 }}>Channel</th>
                            <th style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 700 }}>Visible</th>
                            {showAvailable && <th style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 700 }}>Available</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {card.cells.map((cell) => (
                            <tr key={cell.channel} style={{ borderTop: '1px solid var(--divider)' }}>
                                <td style={{ padding: '7px 6px', fontSize: 12.5, fontWeight: 600, color: 'var(--t1)' }}>{cell.channelName ?? chat.channelName(cell.channel)}</td>
                                <td style={{ textAlign: 'center', padding: '7px 6px' }}><Dot on={cell.visible} color="#7C3AED" /></td>
                                {showAvailable && <td style={{ textAlign: 'center', padding: '7px 6px' }}><Dot on={!!cell.available} color="var(--green)" /></td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
                );
            })()}
            {card.mode === 'item-list' && card.items && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {card.items.map((it, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontFamily: FONT }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>{it.name}</span>
                            {it.detail && <Pill tone={it.tone ?? 'info'}>{it.detail}</Pill>}
                        </div>
                    ))}
                </div>
            )}
            {card.mode === 'session' && card.sessionLines && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {card.sessionLines.map((l, i) => (
                        <div key={i} style={{ fontFamily: FONT }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>{l.label}</div>
                            <div style={{ fontSize: 12, color: 'var(--t2)', marginTop: 1 }}>{l.detail}</div>
                        </div>
                    ))}
                </div>
            )}
            {card.footnote && <div style={{ marginTop: 11, fontSize: 11, color: 'var(--t3)', fontFamily: FONT, lineHeight: 1.4 }}>{card.footnote}</div>}
        </div>
    </CardShell>
);

/* ─────────────────────────── Diagnostic card (§5.4) ─────────────────────────── */

const DiagnosticCardView: React.FC<{ card: PCDiagnosticCard; chat: PCChatbot; onSend: (text: string, fromAffordance?: boolean) => void }> = ({ card, chat, onSend }) => {
    const accent = card.verdict === 'showing' ? 'var(--green)' : 'var(--orange)';
    return (
        <CardShell accent={accent}>
            <Head icon={card.verdict === 'showing' ? 'check_circle' : 'troubleshoot'} title={`${card.entityName} on ${card.channelName ?? chat.channelName(card.channel)}`} subtitle="Resolution chain" accent={accent} />
            <div style={{ padding: '10px 14px 4px', fontSize: 12.5, color: 'var(--t2)', fontFamily: FONT, lineHeight: 1.45 }}>
                <Rich text={card.headline} />
            </div>
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {card.steps.map((s, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '7px 8px', borderRadius: 8, background: s.primary ? 'rgba(217,119,6,0.08)' : 'transparent', fontFamily: FONT }}>
                        <Icon name={s.passed ? 'check_circle' : 'cancel'} size={16} color={s.passed ? 'var(--green)' : 'var(--red)'} style={{ marginTop: 1 }} />
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--t1)' }}>{s.label}{s.primary && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: 'var(--orange)' }}>BLOCKER</span>}</div>
                            {s.detail && <div style={{ fontSize: 11.5, color: 'var(--t3)' }}>{s.detail}</div>}
                        </div>
                    </div>
                ))}
            </div>
            <FixButtons fixes={card.fixes} onSend={onSend} />
        </CardShell>
    );
};

/* ─────────────────────────── Diagnostic-multi card (§5.5) ─────────────────────────── */

/**
 * A blocker line, split into the cause and the "as of now" context that follows it.
 *
 * Several reasons end with a clause about the current moment — "…; it's 3:21 AM local now",
 * "…; today is 2026-09-06". Run together on one line it reads as part of the rule itself and
 * makes an already-long sentence longer. Split at the semicolon so the rule stands on its own
 * line and the timestamp sits under it, quieter — it is context, not another cause.
 */
const ReasonLine: React.FC<{ text: string; muted?: boolean }> = ({ text, muted }) => {
    const at = text.lastIndexOf('; ');
    const rule = at === -1 ? text : text.slice(0, at);
    const context = at === -1 ? '' : text.slice(at + 2);
    return (
        <>
            <div style={{ fontSize: 11.5, color: muted ? 'var(--t3)' : 'var(--t2)', lineHeight: 1.4 }}>{rule}</div>
            {/* Amber and semi-bold: this clause is the "as of now" that makes the rule above
                bite right this minute ("it's 3:34 AM local now"), so it should catch the eye
                rather than fade — it is the difference between a rule and a live problem. */}
            {context && (
                <div style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 600, lineHeight: 1.35, marginTop: 1 }}>{context}</div>
            )}
        </>
    );
};

const DiagnosticMultiCardView: React.FC<{ card: PCDiagnosticMultiCard; chat: PCChatbot; onSend: (text: string, fromAffordance?: boolean) => void }> = ({ card, chat, onSend }) => (
    <CardShell accent="#0EA5E9">
        <Head icon="troubleshoot" title={card.entityName} subtitle="Per-channel status" accent="#0EA5E9" />
        <div style={{ padding: '10px 14px 4px', fontSize: 12.5, color: 'var(--t2)', fontFamily: FONT, lineHeight: 1.45 }}>
            <Rich text={card.headline} />
        </div>
        {/* Causes that hold the item back on EVERY channel (its session window, seasonal dates,
            an inactive item) are stated once here. Repeating them inside all six channel rows
            printed the same long sentence over and over and buried the per-channel detail. */}
        {!!card.itemWideReasons?.length && (
            <div style={{ margin: '0 12px 2px', padding: '9px 11px', borderRadius: 8, background: 'var(--surface-2, rgba(148,163,184,0.10))', border: '1px solid var(--divider)', fontFamily: FONT }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: 0.2, color: 'var(--t2)', textTransform: 'uppercase' }}>
                    <Icon name="info" size={13} color="var(--orange)" />
                    Affects every channel
                </div>
                {card.itemWideReasons.map((reason, ri) => (
                    <div key={ri} style={{ marginTop: 4 }}>
                        <ReasonLine text={reason} />
                    </div>
                ))}
            </div>
        )}
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {card.rows.map((r, i) => {
                // The first reason is the one most worth acting on (the server ranks it), so it
                // reads at full strength; anything after it is supporting detail.
                const reasons = r.reasons?.length ? r.reasons : r.reason ? [r.reason] : [];
                return (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '7px 8px', borderTop: i ? '1px solid var(--divider)' : 'none', fontFamily: FONT }}>
                        <Icon name={r.verdict === 'showing' ? 'check_circle' : 'cancel'} size={16} color={r.verdict === 'showing' ? 'var(--green)' : 'var(--red)'} style={{ marginTop: 1 }} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--t1)' }}>{r.channelName ?? chat.channelName(r.channel)}</div>
                            {/* Every reason reads at the SAME weight. Dimming the later ones
                                implied they mattered less, when they block the item just as
                                hard — the ORDER already carries the priority (the server ranks
                                them: visibility, then out of stock, then schedule). */}
                            {reasons.map((reason, ri) => (
                                <div key={ri} style={{ marginTop: ri ? 3 : 1 }}>
                                    <ReasonLine text={reason} />
                                </div>
                            ))}
                            {/* Blocked with nothing of its own to say: the only cause is the
                                shared one above, so point there instead of leaving a bare row. */}
                            {r.verdict === 'blocked' && reasons.length === 0 && (
                                <div style={{ fontSize: 11.5, color: 'var(--t3)', marginTop: 1, fontStyle: 'italic' }}>Blocked by the reason above</div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
        <FixButtons fixes={card.fixes} onSend={onSend} />
    </CardShell>
);

/* ─────────────────────────── Sync card (§7) ─────────────────────────── */

const groupLabel: Record<string, string> = { device: 'Devices', integration: 'Integrations', direct: 'Direct channels', third_party: 'Third parties' };

const SyncCardView: React.FC<{ msgId: string; card: PCSyncCard; conversationId: string; chat: PCChatbot }> = ({ msgId, card, conversationId, chat }) => {
    const navigate = useNavigate();
    // The saga now owns the whole trigger → countdown → results flow (runSyncCountdown in
    // ProductCatalogChatbotSaga.ts) and ticks `card.summary` live every second via
    // CHATBOT_SYNC_TICK — no component-side polling needed; this just renders whatever the
    // store currently holds.
    const showResults = card.status === 'results' && card.results;
    const groups = Array.from(new Set((card.results ?? []).map((r) => r.group ?? 'other')));
    // A finished sync — auto-synced, or results with nothing left to retry — is a spent
    // record: greyed and inert so "Sync now" / "Retry" can't be re-triggered (§Item 1). An
    // 'offer' stays live (there's a sync to run) and 'results' WITH failures stays live (Retry).
    const spent = card.status === 'auto' || (card.status === 'results' && !card.hasFailures);
    // A 'running' card with no active countdown (the saga only runs one live; on reload there
    // is none) is a stalled sync we can't auto-resume — offer a manual status check instead of
    // a dead "Working…" pill. `syncing` is true only while an active saga countdown is ticking.
    const stalledRunning = card.status === 'running' && !chat.state.syncing;
    // Once a sync has actually happened (running/results/auto), surface a link to the full Sync
    // History page. Rendered OUTSIDE the (frozen) card so it stays clickable even when spent.
    const showHistoryLink = card.status !== 'offer';

    return (
      <div style={{ width: '100%', maxWidth: 460 }}>
        <CardShell accent="var(--green)" spent={spent}>
            <Head
                icon={card.status === 'auto' ? 'cloud_done' : card.status === 'running' ? 'sync' : showResults ? 'fact_check' : 'sync'}
                title={card.status === 'auto' ? 'Synced automatically' : card.status === 'running' ? 'Syncing…' : showResults ? 'Sync results' : 'Sync changes'}
                subtitle={card.status === 'offer' && card.changeCount ? `${card.changeCount} pending` : undefined}
                accent="var(--green)"
            />
            <div style={{ padding: '10px 14px', fontSize: 12.5, color: 'var(--t2)', fontFamily: FONT, lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                <Rich text={card.summary} />
            </div>

            {showResults && (
                <div style={{ padding: '0 14px 10px' }}>
                    {groups.map((g) => {
                        const rows = card.results!.filter((r) => (r.group ?? 'other') === g);
                        if (!rows.length) return null;
                        return (
                            <div key={g} style={{ marginTop: 8 }}>
                                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, fontFamily: FONT }}>{groupLabel[g] ?? g}</div>
                                {rows.map((r, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, padding: '5px 0', fontFamily: FONT }}>
                                        <span style={{ fontSize: 12.5, color: 'var(--t1)', fontWeight: 600 }}>{r.label}</span>
                                        <Icon
                                            name={r.status === 'success' ? 'check_circle' : r.status === 'failed' ? 'error' : 'schedule'}
                                            size={16}
                                            color={r.status === 'success' ? 'var(--green)' : r.status === 'failed' ? 'var(--red)' : 'var(--orange)'}
                                        />
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}

            <div style={{ padding: '10px 14px', borderTop: '1px solid var(--divider)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {card.status === 'offer' && (
                    <PrimaryBtn icon="sync" onClick={() => chat.runSync(conversationId, msgId)} disabled={chat.state.syncing}>Sync now</PrimaryBtn>
                )}
                {card.status === 'running' && (
                    stalledRunning ? (
                        // Resumed after a reload mid-sync — the live countdown saga is gone, so
                        // give the user a way to pull the outcome rather than stranding them.
                        <>
                            <Pill tone="warn"><Icon name="sync" size={13} /> In progress</Pill>
                            <GhostBtn icon="refresh" small onClick={() => chat.fetchSyncResults(conversationId, msgId)}>Check status</GhostBtn>
                        </>
                    ) : (
                        <Pill tone="warn"><Icon name="sync" size={13} /> Working…</Pill>
                    )
                )}
                {card.status === 'auto' && <Pill tone="ok"><Icon name="check" size={13} /> Up to date</Pill>}
                {showResults && (
                    card.hasFailures ? (
                        <>
                            <Pill tone="warn"><Icon name="warning" size={13} /> Some targets failed</Pill>
                            <GhostBtn icon="refresh" small onClick={() => chat.retrySync(conversationId, msgId, card.results ?? [])}>Retry failed</GhostBtn>
                        </>
                    ) : (
                        <Pill tone="ok"><Icon name="check" size={13} /> Synced everywhere</Pill>
                    )
                )}
            </div>
        </CardShell>
        {showHistoryLink && (
            // Navigation aid — kept outside the (possibly spent/frozen) card so it stays
            // clickable, and styled as a link, not a re-runnable action.
            <button
                type="button"
                onClick={() => navigate('/product-catalog/sync-history')}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 7, marginLeft: 2,
                    border: 'none', background: 'transparent', color: 'var(--accent)', cursor: 'pointer',
                    fontSize: 12, fontWeight: 600, fontFamily: FONT, padding: 0,
                }}
            >
                <Icon name="history" size={14} color="var(--accent)" /> View Sync History
            </button>
        )}
      </div>
    );
};

/* ─────────────────────────── Dispatcher ─────────────────────────── */

export const PCMessageView: React.FC<{
    msg: PCMessage;
    chat: PCChatbot;
    conversationId: string;
    onSend: (text: string, fromAffordance?: boolean) => void;
}> = ({ msg, chat, conversationId, onSend }) => {
    switch (msg.kind) {
        case 'bot-text':
            return <div style={{ margin: '4px 0' }}><BotText msg={msg} onSend={onSend} /></div>;
        case 'state':
            return <div style={{ margin: '6px 0' }}><StateCardView card={msg.card} chat={chat} /></div>;
        case 'diagnostic':
            return <div style={{ margin: '6px 0' }}><DiagnosticCardView card={msg.card} chat={chat} onSend={onSend} /></div>;
        case 'diagnostic-multi':
            return <div style={{ margin: '6px 0' }}><DiagnosticMultiCardView card={msg.card} chat={chat} onSend={onSend} /></div>;
        case 'action':
            return <div style={{ margin: '6px 0' }}><ActionCardView card={msg.card} chat={chat} /></div>;
        case 'sync':
            return <div style={{ margin: '6px 0' }}><SyncCardView msgId={msg.id} card={msg.card} conversationId={conversationId} chat={chat} /></div>;
        default:
            return null;
    }
};
